/* eslint-disable @typescript-eslint/consistent-type-imports */
import React from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import {
	ANALYTICS_PAGE_MODE,
	ANALYTICS_UI_SUBJECT_ID,
	type AnalyticsOrigin,
} from '../constants/snippetAnalytics.ts';
import { CreateSnippetModal } from '../modals/CreateSnippetModal.tsx';

import { useSnippetAnalytics } from './useSnippetAnalytics.ts';

export type UseHandleCreateSnippetProps = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPIRef?: React.RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

/**
 * Hook that returns a handler for creating a snippet from the current editor selection.
 * Pass editorAPIRef from the caller (e.g. a ref holding the fullPageEditorPresetAPI).
 *
 * @returns A handler that accepts an `origin` (e.g. CONTEXTUAL_TOOLBAR, BLOCK_MENU)
 *          to identify which UI surface triggered the create action.
 */
export const useHandleCreateSnippet = ({
	contentId,
	spaceId,
	spaceKey,
	editorAPIRef,
}: UseHandleCreateSnippetProps): ((origin: AnalyticsOrigin) => void) => {
	const { fireButtonClickedAnalytic } = useSnippetAnalytics();
	const { showModal } = useDialogs();
	const isLivePage = useIsCurrentPageLive();

	return (origin: AnalyticsOrigin) => {
		const editorAPI = editorAPIRef?.current;
		if (!editorAPI) {
			return;
		}

		const actions = editorAPI.selectionExtension?.actions;
		const document = actions?.getDocumentFromSelection?.();
		const docAdf = document?.selectedNodeAdf;

		// getDocumentFromSelection will always return adf of type 'doc',
		// so strip this doc wrapper for the saved content
		const adfContent = docAdf?.content?.filter((content: unknown) => content !== undefined);
		if (!adfContent || adfContent.length === 0) {
			return;
		}

		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.CREATE_SNIPPET, {
			origin,
			pageMode: isLivePage ? ANALYTICS_PAGE_MODE.LIVE : ANALYTICS_PAGE_MODE.EDIT,
			selectionLength: JSON.stringify(adfContent)?.length,
		});

		showModal(CreateSnippetModal, {
			adf: docAdf,
			contentId,
			spaceId,
			spaceKey,
			editorAPI,
		});
	};
};
